import { type } from 'arktype';
import { dayKeySchema } from './day-key.model';
import type { DeviationReason } from './my-week.model';
import { overviewRevisionsSchema, type Revisions } from './overview.model';
import type { BookingJournalSource } from './person-journal.model';

/** Flag severities that surface in the review queue (`pass` verdicts stay internal). */
export const reviewQueueSeverities = ['warn', 'block'] as const;
export type ReviewQueueSeverity = (typeof reviewQueueSeverities)[number];

/**
 * Latest warn/block verdict of the day, one per rule instance.
 * `explanationEmployee`/`explanationHr` are i18n keys; the interpolation
 * parameters are the persisted `computedValues` (frozen at evaluation time).
 * `ruleKindKey` drives the plain-language value lines of the detail panel
 * (spec §3d-14) — raw `computedValues` stay behind the technical expander.
 */
export interface ReviewQueueVerdict {
  id: string;
  ruleInstanceId: string;
  /** RuleKind template key, e.g. 'min_rest_between_shifts'. */
  ruleKindKey: string;
  severity: ReviewQueueSeverity;
  explanationEmployee: string;
  explanationHr: string;
  computedValues: Record<string, number | string>;
  /** Ids of the booking_records that participated in the evaluation (explain panel). */
  contextBookingIds: string[];
}

/** Booking referenced by a verdict's context — the explain panel shows its times. */
export interface ReviewQueueContextBooking {
  id: string;
  bookingDate: string;
  source: BookingJournalSource;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
}

/** One `needs_review` booking inside a day bundle (spec §3d-11). */
export interface ReviewQueueBooking {
  bookingId: string;
  source: BookingJournalSource;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  /** |actual − plan| in net minutes (0 when the booking only carries rule flags). */
  deviationMinutes: number;
  deviationReason: DeviationReason | null;
}

/**
 * One review case = ONE person + day bundle (spec §3d-11): all `needs_review`
 * bookings of the day plus the day's latest warn/block verdicts. Approve and
 * reject decide the WHOLE day.
 */
export interface ReviewQueueItem {
  personId: string;
  firstName: string;
  lastName: string;
  bookingDate: string;
  /** All `needs_review` bookings of the day (at least one). */
  bookings: ReviewQueueBooking[];
  /** Sum of the bookings' deviations in net minutes. */
  deviationMinutes: number;
  /** First employee reason of the day's bookings; null when none was given. */
  deviationReason: DeviationReason | null;
  /** Highest flag of the day: block > warn; null = pure deviation without flags. */
  severity: ReviewQueueSeverity | null;
  verdicts: ReviewQueueVerdict[];
  /** Bookings referenced by the verdicts' contextBookingIds (explain panel). */
  contextBookings: ReviewQueueContextBooking[];
  revisions: Revisions;
}

/** Identifies one person+day bundle of the queue. */
export const reviewDayKeySchema = type({
  personId: 'string.uuid',
  bookingDate: dayKeySchema,
});
export type ReviewDayKey = typeof reviewDayKeySchema.t;

/**
 * Body of `POST /time-tracking/review-queue/days/approve` (spec §3d-11/13).
 * `justification` is mandatory (server-enforced 400) when the day carries a
 * warn/block verdict.
 */
export const approveDaySchema = type({
  personId: 'string.uuid',
  bookingDate: dayKeySchema,
  'justification?': 'string <= 1000',
  expected: overviewRevisionsSchema,
});
export type ApproveDayInput = typeof approveDaySchema.t;

/**
 * Body of `POST /time-tracking/review-queue/days/reject` (spec §3d-12).
 * The comment is mandatory — rejecting without one is a 400.
 */
export const rejectDaySchema = type({
  personId: 'string.uuid',
  bookingDate: dayKeySchema,
  comment: '1 <= string <= 1000',
  expected: overviewRevisionsSchema,
});
export type RejectDayInput = typeof rejectDaySchema.t;

/**
 * Body of `POST /time-tracking/review-queue/bulk-approve` (spec §3d-13, the
 * "approve without rule violation" action). The server re-checks every day's
 * verdicts and skips days carrying ANY warn/block flag — bulk never covers
 * violations.
 */
export const bulkApproveDaysSchema = type({
  days: reviewDayKeySchema.onUndeclaredKey('reject').array().atLeastLength(1).atMostLength(500),
});
export type BulkApproveDaysInput = typeof bulkApproveDaysSchema.t;

/** Result of a day approve/reject decision. */
export interface ReviewDayDecisionResponse {
  personId: string;
  bookingDate: string;
  reviewStatus: 'approved' | 'rejected';
  /** Ids of the bookings that transitioned with the day. */
  bookingIds: string[];
}

export interface BulkApproveDaysSkippedEntry {
  personId: string;
  bookingDate: string;
  /**
   * `flagged` = the day carries a warn/block verdict (needs an individual,
   * justified decision); `own` = four-eyes rule (F16d); `not_pending` = the
   * day has no open `needs_review` booking (already decided elsewhere);
   * `out_of_scope` = the person is outside the reviewer's responsibility
   * scope (F18, spec §3e-24).
   */
  reason: 'not_pending' | 'flagged' | 'own' | 'out_of_scope';
}

export interface BulkApproveDaysResponse {
  approvedDays: ReviewDayKey[];
  skipped: BulkApproveDaysSkippedEntry[];
}

/**
 * Response of `GET /time-tracking/review-queue/count`: number of open
 * person+day bundles — the data source of the "Freigaben" nav badge
 * (bundles, not bookings; spec §3d-11).
 */
export interface ReviewQueueCount {
  count: number;
}

/**
 * Body of `PATCH /time-tracking/booking-records/:id/correct` (F24-3, spec §3h).
 * Supervisor correction: all time fields are optional — only supplied fields
 * are changed. `comment` is a mandatory audit note (visible in the day detail).
 * Sets `entry_method = 'manual_supervisor'` and `review_status = 'needs_review'`
 * unless the correcting supervisor is also the approver for the person (four-eyes
 * respected — never own person).
 */
export const supervisorCorrectionSchema = type({
  'startTime?': 'string',
  'endTime?': 'string',
  'breakMinutes?': 'number.integer >= 0',
  'homeOffice?': 'boolean',
  'note?': 'string | null',
  comment: '1 <= string <= 1000',
});
export type SupervisorCorrectionInput = typeof supervisorCorrectionSchema.t;

export interface SupervisorCorrectionResponse {
  bookingRecordId: string;
  personId: string;
  bookingDate: string;
  reviewStatus: 'needs_review' | 'approved';
}
